import { useState, type Key } from "react";
import {
  useQuery,
  useSuspenseQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Modal, Table, Button, Space } from "antd";
import { projectQueries } from "@entities/project";
import { oldBaseApi } from "@shared/api";
import { env } from "@shared/config";
import { useCoordinator } from "@shared/lib/hooks";
import { InputSearch } from "@shared/ui";
import { fallbackString, formatDate } from "@shared/utils";
import { DocumentModalTitleCell } from "./document-modal-title-cell";
import "./generic-document-modal.scss";

export interface DocumentItem {
  key: string;
  title: string;
  code: string;
  date: string;
  pages: number;
  originalData: any;
}

interface WorkingDraftResponse {
  entityList: any[];
  pageCount: number;
  pageNumber: number;
  pageSize: number;
}

interface GenericDocumentModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (selected: DocumentItem | null) => void;
  docType: string;
  endpoint: string;
  usePost?: boolean;
  singleSelect?: boolean;
  title?: string;
}

export const GenericDocumentModal = ({
  visible,
  onCancel,
  onConfirm,
  docType,
  endpoint,
  usePost = false,
  singleSelect = true,
  title = "Выберите документ",
}: GenericDocumentModalProps) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { projectId } = useCoordinator();
  const queryClient = useQueryClient();
  const { data: project } = useSuspenseQuery(
    projectQueries.getProject({ projectId }),
  );

  const { data, isLoading, isPending } = useQuery<WorkingDraftResponse, Error>({
    queryKey: ["generic-docs", docType, search, currentPage, pageSize],
    queryFn: async () => {
      const commonParams = {
        projectId: project.externalId,
        pageNumber: currentPage - 1,
        pageSize,
        searchValue: search,
        sortField: "creationDate",
        direction: "DESC",
      };

      const response = usePost
        ? await oldBaseApi.post<WorkingDraftResponse>(
            `/filestore/api/v1/${endpoint}/search`,
            commonParams,
          )
        : await oldBaseApi.get<WorkingDraftResponse>(
            `/filestore/api/v1/${endpoint}`,
            {
              params: {
                ...commonParams,
                sortDirection: commonParams.direction,
              },
            },
          );

      return response.data;
    },
    enabled: visible,
  });

  const tableData: DocumentItem[] =
    data?.entityList.map((doc) => ({
      key: doc.id,
      title: doc.name,
      code: doc.number ?? doc.cipher ?? "",
      date: doc.issueDate,
      pages: doc.pdfPagesCount,
      originalData: doc,
    })) ?? [];

  const filteredData = tableData.filter((doc) =>
    doc.title.toLowerCase().includes(search.toLowerCase()),
  );

  const columns = [
    {
      title: "Документ",
      dataIndex: "title",
      key: "title",
      render: (text: string, record: DocumentItem) => (
        <DocumentModalTitleCell
          title={text}
          originalData={record.originalData}
        />
      ),
    },
    {
      title: "Шифр",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Дата выдачи",
      dataIndex: "date",
      key: "date",
      render: (date: string) => fallbackString(formatDate(date)),
    },
    {
      title: "Листы",
      dataIndex: "pages",
      key: "pages",
      render: (pages: number) => <div>{pages}</div>,
    },
  ];

  const rowSelection = {
    type: singleSelect ? ("radio" as const) : ("checkbox" as const),
    selectedRowKeys,
    onChange: (keys: Key[]) => setSelectedRowKeys(keys),
  };

  const handleConfirm = () => {
    if (singleSelect && selectedRowKeys.length > 0) {
      const selected = tableData.find((doc) => doc.key === selectedRowKeys[0]);
      onConfirm(selected || null);
    } else {
      const selected = tableData.filter((doc) =>
        selectedRowKeys.includes(doc.key),
      );
      onConfirm(selected.length > 0 ? selected[0] : null);
    }
    setSelectedRowKeys([]);
    setSearch("");
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        onCancel();
        setSelectedRowKeys([]);
        setSearch("");
      }}
      width={1024}
      title={title}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            onCancel();
            setSelectedRowKeys([]);
            setSearch("");
          }}
        >
          Отменить
        </Button>,
        <Button
          key="confirm"
          type="primary"
          disabled={selectedRowKeys.length === 0}
          onClick={handleConfirm}
        >
          Подтвердить выбор
        </Button>,
      ]}
    >
      <InputSearch
        placeholder="Поиск по наименованию"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 16, width: "25%" }}
      />

      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Button
          onClick={() => {
            const url = new URL(
              `${env.OLD_FRONTEND_URL}/projects/${project.externalId}/contracts/${project.externalId}/docstore/add-file/`,
            );

            url.searchParams.set("tab", endpoint);

            if (endpoint === "permits") {
              url.searchParams.set("documentType", "PERMIT");
              url.searchParams.set("isStrictDocumentType", "true");
            }

            window.open(url.toString(), "_blank", "noopener,noreferrer");
          }}
          type="default"
          className="upload-document-btn"
        >
          Загрузить документ
        </Button>

        <Button
          onClick={() => {
            queryClient.invalidateQueries({
              queryKey: ["generic-docs", docType, search, currentPage, pageSize],
            });
          }}
          type="link"
        >
          Обновить список
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowSelection={rowSelection}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.pageCount ? data.pageCount * pageSize : 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
        className="sub-documents-table"
        loading={isLoading || isPending}
      />
    </Modal>
  );
};

